// src/components/HeroStack.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./styles/HeroStack.css";

/**
 * Layered hero image stack.
 * - Shows up to 3 images as "cards"
 * - Click any card to bring it to the top
 * - Auto-rotates through the cards using the same animation as a click
 * - Renders ghost layers for depth if <3 images
 */
export default function HeroStack({
  images = [],
  className = "",
  autoRotate = true,
  intervalMs = 4000,
  pauseOnHover = true,
}) {
  const imgs = useMemo(() => {
    if (!Array.isArray(images)) return [];
    return images.filter((x) => x && x.src);
  }, [images]);

  const top = imgs.slice(0, 3);
  const n = top.length;

  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);

  // Keep active valid if images load async / change
  useEffect(() => {
    if (n === 0) return;
    if (active >= n) setActive(0);
  }, [n, active]);

  const advance = useCallback(() => {
    setActive((prev) => (prev + 1) % n);
  }, [n]);

  // Auto-rotate: reuses the exact same setActive() call a click makes,
  // so the CSS transition (rank -> hero-card--r0/r1/r2) is identical.
  useEffect(() => {
    if (!autoRotate || n < 2) return;
    if (pauseOnHover && hovered) return;

    const id = setInterval(advance, intervalMs);
    return () => clearInterval(id);
  }, [autoRotate, pauseOnHover, hovered, intervalMs, n, advance]);

  if (!n) return null;

  // Always render 3 layers visually
  const slots = [0, 1, 2].map((slotIndex) => top[slotIndex] || null);

  const rankFor = (slotIndex) => {
    // rank 0 = top, 1 = middle, 2 = back
    // Only meaningful for real cards within [0..n-1]
    return (slotIndex - active + n) % n;
  };

  return (
    <div
      className={`hero-stack ${className}`}
      role="group"
      aria-label="Hero images"
      onMouseEnter={() => pauseOnHover && setHovered(true)}
      onMouseLeave={() => pauseOnHover && setHovered(false)}
    >
      {slots.map((img, slotIndex) => {
        // If it's a real image card, compute its stack rank relative to active
        const isReal = Boolean(img);
        const rank = isReal ? rankFor(slotIndex) : slotIndex; // ghosts use their slot depth
        const stackClass = `hero-card hero-card--r${Math.min(rank, 2)} ${!isReal ? "hero-card--ghost" : ""}`;

        if (!isReal) {
          return <div key={`ghost-${slotIndex}`} className={stackClass} aria-hidden="true" />;
        }

        return (
          <button
            key={`${img.src}-${slotIndex}`}
            type="button"
            className={stackClass}
            onClick={() => setActive(slotIndex)}
            aria-label={img.ariaLabel || `Bild ${slotIndex + 1}`}
          >
            <img src={img.src} alt={img.alt || ""} loading="eager" />
          </button>
        );
      })}
    </div>
  );
}